// src/pages/creator-panel/LivePhoneCall/LivePhoneCall.js
// Per-chatbot configuration page for the real-telephony channel.
// Mirrors LiveConversation.js (RAG toggle, greeting, prompt, sources,
// manual content, variables) and adds telephony-specific fields:
//   - Phone provider selection (from shared SmsServiceConfig records
//     whose `channels` include 'phone')
//   - Inbound phone number (E.164)
//   - Outbound calls toggle
//   - Manage Tools button (routes to the existing Live Conversation Tools
//     page — tools are a single shared pool with three channel toggles)
import React, { useState, useEffect, useCallback } from 'react';
import { useEditor } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Underline from '@tiptap/extension-underline';
import axiosInstance from '../../../utils/axiosInstance';
import { useChatbots } from '../../../contexts/ChatbotContext';
import { hasPermission } from '../../../utils/permissionUtils';
import TiptapEditor from '../../../components/Common/TiptapEditor';
import {
    Box, Typography, Alert, Button, Switch, FormControlLabel, TextField, CircularProgress,
    Card, CardContent, Tabs, Tab, Checkbox, LinearProgress, Chip, IconButton, Tooltip,
    Divider, InputAdornment, Select, MenuItem, FormControl, InputLabel,
} from '@mui/material';
import {
    Save as SaveIcon, Language as WebIcon, Description as DocIcon, CloudQueue as DriveIcon,
    Edit as EditIcon, Search as SearchIcon, Delete as DeleteIcon, CheckCircle as CheckCircleIcon,
    Build as ToolIcon, Code as VarIcon, Phone as PhoneIcon, AddCircleOutline as AddIcon,
    FiberManualRecord as RecordIcon, SettingsPhone as ProviderIcon,
} from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';

const MAX_CHARACTERS = 20000;

export default function LivePhoneCall() {
    const navigate = useNavigate();
    const { chatbotId: urlChatbotId } = useParams();
    const { activeChatbot } = useChatbots();
    // The dashboard's chatbot-scoped layout lives at `/chatbot/:chatbotId/*`,
    // so every navigate() needs the same prefix. Prefer URL param, fall back
    // to the context's active chatbot.
    const chatbotId = urlChatbotId || activeChatbot?.chatbot_identity;
    const scope = `/chatbot/${chatbotId}`;

    const [canView, setCanView] = useState(false);
    const [canModify, setCanModify] = useState(false);
    const [checkingPermissions, setCheckingPermissions] = useState(true);

    // Same shape as LiveConversation
    const [ragEnabled, setRagEnabled] = useState(true);
    const [greetingMessage, setGreetingMessage] = useState('Hello, how can I help you?');
    const [promptText, setPromptText] = useState('');
    const [selectedSources, setSelectedSources] = useState([]);
    const [variableRows, setVariableRows] = useState([]);
    const [availableSources, setAvailableSources] = useState([]);
    const [sourceTab, setSourceTab] = useState(0);
    const [searchText, setSearchText] = useState('');
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [loadingSources, setLoadingSources] = useState(false);
    const [message, setMessage] = useState(null);

    // Phone-specific fields
    const [phoneProviders, setPhoneProviders] = useState([]);
    const [serviceConfigId, setServiceConfigId] = useState('');
    const [inboundNumber, setInboundNumber] = useState('');
    const [outboundEnabled, setOutboundEnabled] = useState(false);

    // Cold-transfer (human handoff): when the AI can't answer — or the caller
    // asks for a human — the live call is redirected to this real number.
    const [transferEnabled, setTransferEnabled] = useState(false);
    const [transferNumber, setTransferNumber] = useState('');

    // Per-chatbot voice provider ('' = use the klonit .env default).
    const [sttProvider, setSttProvider] = useState('');

    const sourceTypes = ['website', 'document', 'gdrive'];
    const sourceTabLabels = ['Website', 'Documents', 'Google Drive'];
    const sourceTabIcons = [<WebIcon />, <DocIcon />, <DriveIcon />];

    // Manual content editor (Tiptap) — same as Live Conversation
    const [manualContent, setManualContent] = useState('');
    const manualEditor = useEditor({
        extensions: [StarterKit, Underline],
        content: '',
        onUpdate: ({ editor }) => setManualContent(editor.getHTML()),
    });

    useEffect(() => {
        let active = true;
        (async () => {
            const v = await hasPermission('live-phone-call', 'view');
            const a = await hasPermission('live-phone-call', 'add');
            const e = await hasPermission('live-phone-call', 'edit');
            if (!active) return;
            setCanView(v);
            setCanModify(a || e);
            setCheckingPermissions(false);
        })();
        return () => { active = false; };
    }, []);

    const computeTotalChars = useCallback(() => {
        const manualLen = manualContent ? manualContent.replace(/<[^>]+>/g, '').length : 0;
        return selectedSources.reduce((sum, s) => sum + (s.character_count || 0), manualLen);
    }, [manualContent, selectedSources]);

    const fetchSettings = useCallback(async () => {
        if (!chatbotId) return;
        setLoading(true);
        try {
            const res = await axiosInstance.get(`/api/chatbots/${chatbotId}/live-phone-call`);
            const data = res?.data?.data;
            if (data) {
                setRagEnabled(!!data.rag_enabled);
                setGreetingMessage(data.greeting_message || 'Hello, how can I help you?');
                setPromptText(data.prompt_text || '');
                setManualContent(data.manual_content || '');
                manualEditor?.commands?.setContent(data.manual_content || '');
                setSelectedSources(data.sources || []);
                setServiceConfigId(data.service_config_id || '');
                setInboundNumber(data.inbound_phone_number || '');
                setOutboundEnabled(!!data.outbound_enabled);
                setTransferEnabled(!!data.transfer_enabled);
                setTransferNumber(data.transfer_phone_number || '');
                setSttProvider(data.stt_provider || '');
                const vv = data.variable_values || {};
                setVariableRows(Object.entries(vv).map(([k, v]) => ({ key: k, value: v })));
            }
        } catch (e) {
            setMessage({ kind: 'error', text: 'Failed to load Live Phone Call settings.' });
        } finally {
            setLoading(false);
        }
    }, [chatbotId, manualEditor]);

    const fetchPhoneProviders = useCallback(async () => {
        try {
            const res = await axiosInstance.get('/sms/service/configs', { params: { channel: 'phone' } });
            setPhoneProviders(res?.data?.data || []);
        } catch (e) { /* tolerate */ }
    }, []);

    const fetchAvailableSources = useCallback(async (page = 1) => {
        if (!chatbotId) return;
        setLoadingSources(true);
        try {
            const res = await axiosInstance.get(
                `/api/chatbots/${chatbotId}/live-phone-call/sources`,
                { params: { source_type: sourceTypes[sourceTab], page, limit: 20, search: searchText } },
            );
            setAvailableSources(res?.data?.data || []);
        } finally {
            setLoadingSources(false);
        }
    }, [chatbotId, sourceTab, searchText]);

    useEffect(() => {
        if (!checkingPermissions && canView) {
            fetchSettings();
            fetchPhoneProviders();
        }
    }, [checkingPermissions, canView, fetchSettings, fetchPhoneProviders]);

    useEffect(() => {
        if (!checkingPermissions && canView) fetchAvailableSources(1);
    }, [checkingPermissions, canView, sourceTab, searchText, fetchAvailableSources]);

    const toggleSource = (src) => {
        const exists = selectedSources.find(s => s.source_id === src.source_id && s.source_type === src.source_type);
        if (exists) setSelectedSources(selectedSources.filter(s => !(s.source_id === src.source_id && s.source_type === src.source_type)));
        else setSelectedSources([...selectedSources, src]);
    };

    const removeSelected = (src) => toggleSource(src);

    const addVarRow = () => setVariableRows([...variableRows, { key: '', value: '' }]);
    const updateVarRow = (i, field, val) => {
        const next = [...variableRows];
        next[i] = { ...next[i], [field]: val };
        setVariableRows(next);
    };
    const removeVarRow = (i) => setVariableRows(variableRows.filter((_, idx) => idx !== i));

    const handleSave = async () => {
        // The content budget only applies in source mode — when RAG is on, the
        // (hidden) sources/manual content aren't used, so don't block the save on it.
        if (!ragEnabled && computeTotalChars() > MAX_CHARACTERS) {
            setMessage({ kind: 'error', text: `Total content exceeds ${MAX_CHARACTERS.toLocaleString()} characters.` });
            return;
        }
        // E.164 (required '+', non-zero leading digit, 7..15 digits total).
        const e164 = /^\+[1-9]\d{6,14}$/;
        const trimmedTransfer = (transferNumber || '').trim();
        if (trimmedTransfer && !e164.test(trimmedTransfer)) {
            setMessage({ kind: 'error', text: 'Transfer phone number must be in E.164 format (e.g. +12245551234).' });
            return;
        }
        if (transferEnabled && !trimmedTransfer) {
            setMessage({ kind: 'error', text: 'A transfer phone number is required when call transfer is enabled.' });
            return;
        }
        setSaving(true);
        try {
            const variable_values = {};
            variableRows.forEach(r => { if (r.key && r.key.trim()) variable_values[r.key.trim()] = r.value || ''; });
            await axiosInstance.post(`/api/chatbots/${chatbotId}/live-phone-call`, {
                rag_enabled: ragEnabled,
                greeting_message: greetingMessage,
                prompt_text: promptText,
                manual_content: manualContent,
                sources: selectedSources,
                variable_values,
                service_config_id: serviceConfigId || null,
                inbound_phone_number: inboundNumber || null,
                outbound_enabled: outboundEnabled,
                transfer_enabled: transferEnabled,
                transfer_phone_number: trimmedTransfer || null,
                stt_provider: sttProvider || null,
            });
            setMessage({ kind: 'success', text: 'Live Phone Call settings saved.' });
        } catch (e) {
            const msg = e?.response?.data?.message || 'Save failed.';
            setMessage({ kind: 'error', text: msg });
        } finally {
            setSaving(false);
        }
    };

    if (checkingPermissions) return <Box sx={{ p: 4, textAlign: 'center' }}><CircularProgress /></Box>;
    if (!canView) return <Alert severity="warning" sx={{ m: 3 }}>You don't have permission to view Live Phone Call settings.</Alert>;
    if (loading) return <Box sx={{ p: 4, textAlign: 'center' }}><CircularProgress /></Box>;

    const totalChars = computeTotalChars();
    const pct = Math.min(100, (totalChars / MAX_CHARACTERS) * 100);

    return (
        <Box sx={{ p: { xs: 2, md: 4 }, maxWidth: 1200, mx: 'auto' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3, flexWrap: 'wrap', gap: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <PhoneIcon color="primary" />
                    <Typography variant="h5">Live Phone Call</Typography>
                </Box>
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                    <Button
                        startIcon={<ProviderIcon />}
                        variant="outlined"
                        onClick={() => navigate(`${scope}/phone-providers`)}
                    >
                        Manage Phone Providers
                    </Button>
                    <Button
                        startIcon={<ToolIcon />}
                        variant="outlined"
                        onClick={() => navigate(`${scope}/live-conversation/tools`)}
                    >
                        Manage Tools
                    </Button>
                    <Button
                        startIcon={<RecordIcon />}
                        variant="outlined"
                        color="error"
                        onClick={() => navigate(`${scope}/live-phone-call/recordings`)}
                    >
                        Call Recordings
                    </Button>
                </Box>
            </Box>

            {message && (
                <Alert severity={message.kind} sx={{ mb: 2 }} onClose={() => setMessage(null)}>
                    {message.text}
                </Alert>
            )}

            {/* Provider + Telephony */}
            <Card sx={{ mb: 3 }}>
                <CardContent>
                    <Typography variant="h6" sx={{ mb: 2 }}>Telephony Provider</Typography>
                    <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: { md: '1fr 1fr' } }}>
                        <FormControl fullWidth disabled={!canModify}>
                            <InputLabel id="phone-provider-label">Phone Provider</InputLabel>
                            <Select
                                labelId="phone-provider-label"
                                value={serviceConfigId || ''}
                                label="Phone Provider"
                                onChange={(e) => setServiceConfigId(e.target.value)}
                            >
                                <MenuItem value=""><em>None (configure first)</em></MenuItem>
                                {phoneProviders.map(p => (
                                    <MenuItem key={p.id} value={p.id}>
                                        {p.service_type} {p.channels?.includes('sms') ? '(shared with SMS)' : ''}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                        <TextField
                            label="Inbound phone number (E.164, e.g. +12245551234)"
                            value={inboundNumber}
                            onChange={(e) => setInboundNumber(e.target.value)}
                            disabled={!canModify}
                            fullWidth
                        />
                        <FormControlLabel
                            control={<Switch checked={outboundEnabled} onChange={(e) => setOutboundEnabled(e.target.checked)} disabled={!canModify} />}
                            label="Enable outbound calls"
                        />
                    </Box>
                </CardContent>
            </Card>

            {/* Call Transfer (Human Handoff) */}
            <Card sx={{ mb: 3 }}>
                <CardContent>
                    <Typography variant="h6" sx={{ mb: 1 }}>Call Transfer (Human Handoff)</Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        When the AI can't answer — or the caller asks for a human — the live call is
                        transferred to this number (a cold/blind transfer; the AI drops off). Twilio only.
                    </Typography>
                    <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: { md: '1fr 1fr' }, alignItems: 'center' }}>
                        <FormControlLabel
                            control={<Switch checked={transferEnabled} onChange={(e) => setTransferEnabled(e.target.checked)} disabled={!canModify} />}
                            label="Enable transfer to a human"
                        />
                        <TextField
                            label="Transfer phone number (E.164, e.g. +12245551234)"
                            value={transferNumber}
                            onChange={(e) => setTransferNumber(e.target.value)}
                            disabled={!canModify}
                            required={transferEnabled}
                            fullWidth
                        />
                    </Box>
                </CardContent>
            </Card>

            {/* Voice Provider (STT) */}
            <Card sx={{ mb: 3 }}>
                <CardContent>
                    <Typography variant="h6" sx={{ mb: 1 }}>Voice Provider</Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        How this chatbot understands and speaks on phone calls. Leave on
                        "Use default" to follow the server setting. (Model, voice and other
                        tuning are configured by your administrator.)
                    </Typography>
                    <FormControl fullWidth disabled={!canModify}>
                        <InputLabel id="phone-stt-provider-label">Voice Provider</InputLabel>
                        <Select
                            labelId="phone-stt-provider-label"
                            value={sttProvider}
                            label="Voice Provider"
                            onChange={(e) => setSttProvider(e.target.value)}
                        >
                            <MenuItem value=""><em>Use default (server setting)</em></MenuItem>
                            <MenuItem value="google">Google Cloud TTS</MenuItem>
                            <MenuItem value="gemini">Gemini LLM</MenuItem>
                            <MenuItem value="live">Gemini Live</MenuItem>
                        </Select>
                    </FormControl>
                </CardContent>
            </Card>

            {/* RAG + Greeting + Prompt */}
            <Card sx={{ mb: 3 }}>
                <CardContent>
                    <FormControlLabel
                        control={<Switch checked={ragEnabled} onChange={(e) => setRagEnabled(e.target.checked)} disabled={!canModify} />}
                        label="Use default knowledgebase (RAG)"
                    />
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        When off, the chatbot relies only on the data sources + manual content selected below.
                    </Typography>
                    <TextField
                        label="Greeting message (spoken via TTS when the call connects)"
                        value={greetingMessage}
                        onChange={(e) => setGreetingMessage(e.target.value)}
                        disabled={!canModify}
                        fullWidth sx={{ mb: 2 }}
                    />
                    <TextField
                        label="Custom prompt instructions for this chatbot"
                        value={promptText}
                        onChange={(e) => setPromptText(e.target.value)}
                        disabled={!canModify}
                        fullWidth multiline minRows={4} maxRows={10}
                    />
                </CardContent>
            </Card>

            {/* Source mode only — the content budget, Data Sources, and Manual
                Content apply ONLY when the default knowledgebase (RAG) is OFF. */}
            {!ragEnabled && (
            <>
            {/* Character budget */}
            <Card sx={{ mb: 3 }}>
                <CardContent>
                    <Typography variant="subtitle2" sx={{ mb: 1 }}>
                        Content budget: {totalChars.toLocaleString()} / {MAX_CHARACTERS.toLocaleString()} chars
                    </Typography>
                    <LinearProgress variant="determinate" value={pct} color={pct >= 100 ? 'error' : 'primary'} />
                </CardContent>
            </Card>

            {/* Source picker */}
            <Card sx={{ mb: 3 }}>
                <CardContent>
                    <Typography variant="h6" sx={{ mb: 1 }}>Data Sources</Typography>
                    <Tabs value={sourceTab} onChange={(_, v) => setSourceTab(v)} sx={{ mb: 2 }}>
                        {sourceTabLabels.map((l, i) => <Tab key={l} label={l} icon={sourceTabIcons[i]} iconPosition="start" />)}
                    </Tabs>
                    <TextField
                        fullWidth size="small" placeholder="Search…"
                        value={searchText} onChange={(e) => setSearchText(e.target.value)}
                        InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon /></InputAdornment> }}
                        sx={{ mb: 2 }}
                    />
                    {loadingSources ? <CircularProgress size={20} /> : (
                        availableSources.length === 0 ? <Typography color="text.secondary">No trained sources for this type.</Typography> : (
                            availableSources.map(src => {
                                const checked = !!selectedSources.find(s => s.source_id === src.source_id && s.source_type === src.source_type);
                                return (
                                    <Box key={`${src.source_type}-${src.source_id}`} sx={{ display: 'flex', alignItems: 'center', py: 0.5 }}>
                                        <Checkbox checked={checked} onChange={() => toggleSource(src)} disabled={!canModify} />
                                        <Typography sx={{ flex: 1 }} noWrap>{src.source_title}</Typography>
                                        <Chip size="small" label={`${(src.character_count || 0).toLocaleString()} chars`} />
                                    </Box>
                                );
                            })
                        )
                    )}
                </CardContent>
            </Card>

            {/* Selected sources chip list */}
            {selectedSources.length > 0 && (
                <Card sx={{ mb: 3 }}>
                    <CardContent>
                        <Typography variant="subtitle1" sx={{ mb: 1 }}>Selected ({selectedSources.length})</Typography>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                            {selectedSources.map(s => (
                                <Chip
                                    key={`${s.source_type}-${s.source_id}`}
                                    label={`${s.source_title} (${(s.character_count || 0).toLocaleString()})`}
                                    onDelete={canModify ? () => removeSelected(s) : undefined}
                                />
                            ))}
                        </Box>
                    </CardContent>
                </Card>
            )}

            {/* Manual content editor */}
            <Card sx={{ mb: 3 }}>
                <CardContent>
                    <Typography variant="h6" sx={{ mb: 2 }}>Manual Content</Typography>
                    <TiptapEditor editor={manualEditor} />
                </CardContent>
            </Card>
            </>
            )}

            {/* Variables */}
            <Card sx={{ mb: 3 }}>
                <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <VarIcon fontSize="small" />
                            <Typography variant="h6">Variables (VAPI-style {'{{var}}'} substitution)</Typography>
                        </Box>
                        <Button startIcon={<AddIcon />} onClick={addVarRow} disabled={!canModify}>Add</Button>
                    </Box>
                    {variableRows.length === 0 && <Typography color="text.secondary">No variables defined.</Typography>}
                    {variableRows.map((r, i) => (
                        <Box key={i} sx={{ display: 'grid', gridTemplateColumns: '1fr 2fr auto', gap: 1, mb: 1 }}>
                            <TextField size="small" label="Name" value={r.key} onChange={(e) => updateVarRow(i, 'key', e.target.value)} disabled={!canModify} />
                            <TextField size="small" label="Value" value={r.value} onChange={(e) => updateVarRow(i, 'value', e.target.value)} disabled={!canModify} />
                            <IconButton onClick={() => removeVarRow(i)} disabled={!canModify}><DeleteIcon /></IconButton>
                        </Box>
                    ))}
                </CardContent>
            </Card>

            <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                <Button
                    variant="contained" size="large" startIcon={<SaveIcon />}
                    onClick={handleSave}
                    disabled={!canModify || saving}
                >
                    {saving ? 'Saving…' : 'Save Settings'}
                </Button>
            </Box>
        </Box>
    );
}
